import useSWR from 'swr';
import { Grid } from '@mui/material';
import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { SERVICES } from '@shared/constants/apiConfig';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { deCamelize, mapGridIndexes, newGridRecordId } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { Wrapper } from '@styles/CommonStyles';
import { ILenderUtilityTabProps } from '../LenderUtilityTabs';
import { TFormula } from '../../interfaces';
import { formulaValidationSchema } from '../../validation';
import { requestCreateFormula, requestDeleteFormula, requestUpdateFormula } from './queries';

export const LTV_TYPE: { [k: string]: string } = {
  frontEnd: 'FrontEnd',
  allIn: 'AllIn',
  backEnd: 'BackEnd',
};

const LTV_TYPES = Object.keys(LTV_TYPE).map((key) => ({
  label: deCamelize(LTV_TYPE[key]),
  value: LTV_TYPE[key],
}));

export const variablesColumns = [
  dataGridCol('path', {
    width: 180,
  }),
  dataGridCol('lenderVariable'),
].map((column) => ({ ...column, flex: 1, resizable: false }));

const formulasColumns = [
  dataGridCol('formulaType', {
    minWidth: 100,
    flex: 1,
    headerName: 'Formula Name',
    renderCell: (params) => deCamelize(params.value),
    renderEditCell: (params) => (
      <GridColSelect
        hideNoneOption
        params={params}
        options={LTV_TYPES}
      />
    ),
  }),
  dataGridCol('formulaValue', {
    headerName: 'Formula Expression',
    minWidth: 300,
    flex: 3,
    renderEditCell: (params) => <GridColTextInput params={params} />,
  }),
].map((column) => ({ ...column, required: true, editable: true }));

type IFormulasFormProps = ILenderUtilityTabProps;

export const Formulas = ({ data, lenderId }: IFormulasFormProps) => {
  const { data: variables } = useSWR(`/${SERVICES.LENDER}/lenders/formula/variables`);

  const gridActions = {
    onSave: (row: TFormula) =>
      row.id === newGridRecordId ? requestCreateFormula(lenderId, row) : requestUpdateFormula(lenderId, row),
    onDelete: (row: TFormula) => requestDeleteFormula(lenderId, row?.id),
  };

  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        md={3}
      >
        <Wrapper>
          <PhoenixGrid
            autoHeight
            noButton
            withHeader
            disableSelectionOnClick
            name="Variable"
            gridId={`Variable-${lenderId}`}
            pageSize={15}
            columns={variablesColumns}
            loading={!variables}
            rows={mapGridIndexes(variables)}
          />
        </Wrapper>
      </Grid>
      <Grid
        item
        md={9}
      >
        <Wrapper>
          <PhoenixGrid
            autoHeight
            withHeader
            name="Formula"
            gridId={`Formula-${lenderId}`}
            pageSize={15}
            columns={formulasColumns}
            loading={!data?.formulas}
            rows={mapGridIndexes(data?.formulas)}
            actions={gridActions}
            editRowsModel={{}}
            validateSchema={formulaValidationSchema}
          />
        </Wrapper>
      </Grid>
    </Grid>
  );
};
